import { supabase } from '../lib/supabase';

export interface KeyMatchEvent {
  id: string;
  match_id: string;
  team_id: string | null; // Made nullable for game flow events
  stat_type_id: string;
  match_minute: number | null; // Made nullable until timer issue is fixed
  primary_player_id?: string;
  secondary_player_id?: string;
  tags: any[];
  status: 'published' | 'unpublished';
  published_at?: string;
  published_by?: string;
  source_log_id?: string;
  event_timestamp?: string; // Added timestamp field
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  
  // Joined data
  team?: {
    id: string;
    name: string;
    logo?: string;
  } | null; // Made nullable for game flow events
  primary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  };
  secondary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  };
  stat_type?: {
    id: string;
    stat_key: string;
    display_name: string;
    category: string;
    event_result: string;
  };
}

export interface PublishEventData {
  source_log_id: string;
  match_id: string;
  team_id: string | null; // Made nullable for game flow events
  stat_type_id: string;
  match_minute: number | null; // Made nullable until timer issue is fixed
  primary_player_id?: string;
  secondary_player_id?: string;
  tags?: any[];
  created_by?: string;
}

export interface KeyEventFilters {
  match_id?: string;
  team_id?: string;
  stat_type_id?: string;
  status?: 'published' | 'unpublished';
  player_id?: string;
  match_minute_from?: number | null;
  match_minute_to?: number | null;
  tags?: string[];
}

export class KeyEventsService {
  /**
   * Publish an event from scoresheet_logs to key_match_events
   */
  static async publishEvent(data: PublishEventData): Promise<KeyMatchEvent> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Insert the published event
      const { data: event, error } = await supabase
        .from('key_match_events')
        .insert({
          match_id: data.match_id,
          team_id: data.team_id,
          stat_type_id: data.stat_type_id,
          match_minute: data.match_minute,
          primary_player_id: data.primary_player_id,
          secondary_player_id: data.secondary_player_id,
          tags: data.tags || [],
          status: 'published',
          published_at: new Date().toISOString(),
          published_by: user?.id,
          source_log_id: data.source_log_id,
          created_by: data.created_by || user?.id,
          is_active: true
        })
        .select(`
          *,
          team:teams(id, name, logo),
          primary_player:players!primary_player_id(id, full_name, kit_number),
          secondary_player:players!secondary_player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .single();

      if (error) {
        throw new Error(`Failed to publish event: ${error.message}`);
      }

      return event;
    } catch (error) {
      console.error('Error publishing event:', error);
      throw error;
    }
  }

  /**
   * Unpublish an event (set status to unpublished)
   */
  static async unpublishEvent(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('key_match_events')
        .update({
          status: 'unpublished',
          published_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId);

      if (error) {
        throw new Error(`Failed to unpublish event: ${error.message}`);
      }
    } catch (error) {
      console.error('Error unpublishing event:', error);
      throw error;
    }
  }

  /**
   * Get key events for a match with optional filters
   */
  static async getKeyEvents(filters: KeyEventFilters): Promise<KeyMatchEvent[]> {
    try {
      console.log('🔍 KeyEventsService.getKeyEvents called with filters:', filters);
      
      // Test Supabase connection first
      const { error: testError } = await supabase
        .from('key_match_events')
        .select('id')
        .limit(1);
      
      if (testError) {
        console.error('❌ Supabase connection test failed:', testError);
        throw new Error(`Supabase connection failed: ${testError.message}`);
      }
      
      console.log('✅ Supabase connection test passed');
      
      let query = supabase
        .from('key_match_events')
        .select(`
          *,
          team:teams(id, name, logo),
          primary_player:players!primary_player_id(id, full_name, kit_number),
          secondary_player:players!secondary_player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .eq('is_active', true)
        .order('event_timestamp', { ascending: true }); // Use timestamp instead of match_minute

      // Apply filters
      if (filters.match_id) {
        query = query.eq('match_id', filters.match_id);
      }
      if (filters.team_id) {
        query = query.eq('team_id', filters.team_id);
      }
      if (filters.stat_type_id) {
        query = query.eq('stat_type_id', filters.stat_type_id);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.player_id) {
        query = query.or(`primary_player_id.eq.${filters.player_id},secondary_player_id.eq.${filters.player_id}`);
      }
      if (filters.match_minute_from !== undefined && filters.match_minute_from !== null) {
        query = query.gte('match_minute', filters.match_minute_from);
      }
      if (filters.match_minute_to !== undefined && filters.match_minute_to !== null) {
        query = query.lte('match_minute', filters.match_minute_to);
      }
      if (filters.tags && filters.tags.length > 0) {
        // Filter by tags using JSONB contains
        const tagFilters = filters.tags.map(tag => `tags.cs.["${tag}"]`).join(',');
        query = query.or(tagFilters);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching key events:', error);
        throw new Error(`Failed to get key events: ${error.message}`);
      }

      console.log('✅ Key events fetched successfully:', data?.length || 0, 'events');
      console.log('📊 Sample event data:', data?.[0]);
      return data || [];
    } catch (error) {
      console.error('Error getting key events:', error);
      throw error;
    }
  }

  /**
   * Get published events for a match (timeline view)
   */
  static async getPublishedEvents(matchId: string): Promise<KeyMatchEvent[]> {
    return this.getKeyEvents({
      match_id: matchId,
      status: 'published'
    });
  }

  /**
   * Get all events for a match (including unpublished)
   */
  static async getAllEvents(matchId: string): Promise<KeyMatchEvent[]> {
    return this.getKeyEvents({
      match_id: matchId
    });
  }

  /**
   * Update event tags
   */
  static async updateEventTags(eventId: string, tags: any[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('key_match_events')
        .update({
          tags,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId);

      if (error) {
        throw new Error(`Failed to update event tags: ${error.message}`);
      }
    } catch (error) {
      console.error('Error updating event tags:', error);
      throw error;
    }
  }

  /**
   * Soft delete an event
   */
  static async deleteEvent(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('key_match_events')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId);

      if (error) {
        throw new Error(`Failed to delete event: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  /**
   * Get event statistics for a match
   */
  static async getEventStats(matchId: string): Promise<{
    total_events: number;
    published_events: number;
    unpublished_events: number;
    events_by_type: Record<string, number>;
    events_by_team: Record<string, number>;
  }> {
    try {
      console.log('📊 KeyEventsService.getEventStats called for match:', matchId);
      const events = await this.getAllEvents(matchId);
      console.log('📊 Events for stats calculation:', events.length);
      
      const stats = {
        total_events: events.length,
        published_events: events.filter(e => e.status === 'published').length,
        unpublished_events: events.filter(e => e.status === 'unpublished').length,
        events_by_type: {} as Record<string, number>,
        events_by_team: {} as Record<string, number>
      };

      events.forEach(event => {
        // Count by event type
        const eventType = event.stat_type?.stat_key || 'unknown';
        stats.events_by_type[eventType] = (stats.events_by_type[eventType] || 0) + 1;
        
        // Count by team
        const teamName = event.team?.name || 'unknown';
        stats.events_by_team[teamName] = (stats.events_by_team[teamName] || 0) + 1;
      });

      console.log('📊 Stats calculated:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error getting event stats:', error);
      throw error;
    }
  }

  /**
   * Publish multiple events at once
   */
  static async publishMultipleEvents(events: PublishEventData[]): Promise<KeyMatchEvent[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const eventsToInsert = events.map(event => ({
        ...event,
        status: 'published' as const,
        published_at: new Date().toISOString(),
        published_by: user?.id,
        created_by: event.created_by || user?.id,
        is_active: true
      }));

      const { data, error } = await supabase
        .from('key_match_events')
        .insert(eventsToInsert)
        .select(`
          *,
          team:teams(id, name, logo),
          primary_player:players!primary_player_id(id, full_name, kit_number),
          secondary_player:players!secondary_player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `);

      if (error) {
        throw new Error(`Failed to publish events: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error publishing multiple events:', error);
      throw error;
    }
  }
}
